import { useEffect, useState } from 'react';
import { ApiError, api, errorText } from '../../lib/api';
import type { Asset, ContentFormat, ContentMeta, ContentStatus, PublicationFile, UsageStatus } from '../../lib/contentTypes';
import type { ReframedAsset } from './Reframe';
import { sha256OfFile } from './sha256';
import './content.css';

export const STATUS_LABEL: Record<ContentStatus, string> = {
  idea: 'idea',
  draft: 'draft',
  needs_review: 'needs review',
  approved: 'approved',
  exported: 'exported',
  published: 'published',
  archived: 'archived',
};
const STATUS_TONE: Record<ContentStatus, string> = {
  idea: 'idle',
  draft: 'idle',
  needs_review: 'warn',
  approved: 'ok',
  exported: 'ok',
  published: 'live',
  archived: 'idle',
};

export const FORMAT_LABEL: Record<ContentFormat, string> = {
  single: 'Single image',
  poster: 'Poster / cover',
  carousel: 'Carousel',
  before_after: 'Before / after',
};

export const USAGE_LABEL: Record<UsageStatus, string> = {
  unreviewed: 'not reviewed',
  approved: 'approved for use',
  restricted: 'restricted',
  do_not_use: 'do not use',
};

export function ContentStatusBadge({ status }: { status: ContentStatus }) {
  return (
    <span className={`cs-status tone-${STATUS_TONE[status]}`} data-status={status}>
      <span className={`sq ${status === 'idea' || status === 'archived' ? 'outline' : 'filled'} tone-${STATUS_TONE[status]}`} aria-hidden="true" /> {STATUS_LABEL[status]}
    </span>
  );
}

let metaCache: ContentMeta | null = null;
export function useContentMeta(): ContentMeta | null {
  const [meta, setMeta] = useState<ContentMeta | null>(metaCache);
  useEffect(() => {
    if (metaCache) return;
    api<ContentMeta>('/api/content/meta').then((m) => {
      metaCache = m;
      setMeta(m);
    });
  }, []);
  return meta;
}

const CHUNK_THRESHOLD = 8_000_000; // above this a file travels in parts (the proxy refuses 25 MB bodies)

async function sha256Hex(buf: ArrayBuffer): Promise<string> {
  const d = await crypto.subtle.digest('SHA-256', buf);
  return Array.from(new Uint8Array(d), (x) => x.toString(16).padStart(2, '0')).join('');
}

/** The numbered parts of one file, then a checked finish: the transport behind every large upload. */
async function sendParts<T>(
  file: File,
  purpose: string,
  params: Record<string, unknown>,
  sha: string,
  progress: (text: string) => void,
): Promise<T> {
  const start = await api<{ id: string; chunk_bytes: number; chunks: number }>('/api/uploads', {
    method: 'POST',
    body: { filename: file.name, size: file.size, purpose, params },
  });
  for (let i = 0; i < start.chunks; i++) {
    progress(`${file.name}: part ${i + 1} of ${start.chunks}`);
    const part = file.slice(i * start.chunk_bytes, (i + 1) * start.chunk_bytes);
    for (let attempt = 0; ; attempt++) {
      try {
        await api(`/api/uploads/${start.id}/chunks/${i}`, { method: 'PUT', raw: part });
        break;
      } catch (e) {
        if (attempt >= 2 || (e instanceof ApiError && e.status >= 400 && e.status < 500)) throw e;
      }
    }
  }
  progress(`${file.name}: checking and storing`);
  return api<T>(`/api/uploads/${start.id}/finish`, { method: 'POST', body: { sha256: sha } });
}

/** One image through POST /api/uploads (the whole file is held once to hash it; images are <= 200 MB). */
async function uploadChunked(file: File, opts: { projectId?: string | null; kind: 'image' | 'logo' }, progress: (text: string) => void): Promise<Asset> {
  if (!globalThis.crypto?.subtle) throw new Error('this browser cannot check large uploads (needs https or localhost)');
  const sha = await sha256Hex(await file.arrayBuffer());
  return sendParts<Asset>(file, 'content_asset', { project_id: opts.projectId ?? null, kind: opts.kind }, sha, progress);
}

/** Videos and other publication files, up to 2 GB: always in parts, hashed in slices, never read whole. */
export async function uploadPublicationFiles(
  files: File[],
  publicationId: string,
  maxMb: number,
  onProgress: (text: string) => void = () => {},
) {
  const done: PublicationFile[] = [];
  const errors: string[] = [];
  for (const [n, file] of files.entries()) {
    const tag = files.length > 1 ? `file ${n + 1} of ${files.length}: ` : '';
    if (file.size > maxMb * 1_000_000) {
      errors.push(`${file.name}: ${(file.size / 1_000_000).toFixed(0)} MB is larger than the ${maxMb} MB limit`);
      continue;
    }
    if (file.size === 0) {
      errors.push(`${file.name}: the file is empty`);
      continue;
    }
    try {
      onProgress(`${tag}${file.name}: reading it to check it`);
      const sha =
        globalThis.crypto?.subtle && file.size <= 256_000_000 ? await sha256Hex(await file.arrayBuffer()) : await sha256OfFile(file);
      done.push(await sendParts<PublicationFile>(file, 'publication_file', { publication_id: publicationId }, sha, (t) => onProgress(tag + t)));
    } catch (e) {
      errors.push(`${file.name}: ${errorText(e)}`);
    }
  }
  onProgress('');
  return { done, errors };
}

export async function uploadAssets(
  files: File[],
  opts: { projectId?: string | null; kind?: 'image' | 'logo'; onProgress?: (text: string) => void } = {},
) {
  const done: Asset[] = [];
  const errors: string[] = [];
  const progress = opts.onProgress ?? (() => {});
  const kind = opts.kind ?? 'image';
  let maxMb = 200;
  try {
    maxMb = (await api<ContentMeta>('/api/content/meta')).max_asset_mb;
  } catch {
    /* the server will refuse an oversize file itself */
  }
  for (const [n, file] of files.entries()) {
    const tag = files.length > 1 ? `file ${n + 1} of ${files.length}: ` : '';
    if (file.size > maxMb * 1_000_000) {
      errors.push(`${file.name}: ${(file.size / 1_000_000).toFixed(1)} MB is larger than the ${maxMb} MB limit; export it smaller`);
      continue;
    }
    try {
      if (file.size > CHUNK_THRESHOLD) {
        done.push(await uploadChunked(file, { projectId: opts.projectId, kind }, (t) => progress(tag + t)));
      } else {
        progress(`${tag}${file.name}: uploading`);
        const form = new FormData();
        form.append('file', file);
        if (opts.projectId) form.append('project_id', opts.projectId);
        form.append('kind', kind);
        done.push(await api<Asset>('/api/content/assets', { method: 'POST', form }));
      }
    } catch (e) {
      errors.push(`${file.name}: ${errorText(e)}`);
    }
  }
  progress('');
  return { done, errors };
}

export function AssetCard({ asset, children, selected, onSelect }: { asset: Asset; children?: React.ReactNode; selected?: number | null; onSelect?: () => void }) {
  return (
    <figure className={`cs-asset${selected != null ? ' selected' : ''}`}>
      {onSelect ? (
        <button type="button" className="cs-asset-pick" onClick={onSelect} aria-pressed={selected != null} aria-label={`Select ${asset.filename}`}>
          <img src={asset.urls.thumb} alt={asset.alt_text || asset.filename} loading="lazy" />
          {selected != null && <span className="cs-pick-n">{selected + 1}</span>}
        </button>
      ) : (
        <img src={asset.urls.thumb} alt={asset.alt_text || asset.filename} loading="lazy" />
      )}
      <figcaption>
        <span className="cs-asset-name" title={asset.filename}>
          {asset.filename}
        </span>
        <span className="muted small num">
          {asset.width}×{asset.height} · {(asset.bytes / 1_000_000).toFixed(1)} MB{asset.has_icc_profile ? ` · ICC ${asset.icc_description ?? ''}` : ' · no ICC profile'}
        </span>
        <span className={`small usage-${asset.usage_status}`}>{USAGE_LABEL[asset.usage_status]}</span>
        {(asset as ReframedAsset).derivation?.kind === 'reframe' && (
          <span className="muted small cs-asset-derived" data-derived-from={(asset as ReframedAsset).derived_from_id ?? ''}>
            reframed from {(asset as ReframedAsset).derivation?.source_filename} · {(asset as ReframedAsset).derivation?.ratio}
          </span>
        )}
        {children}
      </figcaption>
    </figure>
  );
}
